import classNames from "classnames";
import { useMemo, useRef } from "react";
import { TMenuIds } from "../../../../app/state/menuSlice/types";
import { MenuBasic } from "../../../Components/Menu/MenuBasic";
import { useMenuSelectors } from "../../../../app/state/menuSlice/hook/use-menu-selectors";
import { AppMenuItem } from "../../../../shared/components/AppMenu/AppMenuItem";
import { MenuItems, TSidebarActionsMenu } from "../../types";
import { TEntityInfo } from "../../../types/utility";
import { useClickOutside } from "../../../../app/state/menuSlice/hook/use-click-outside";
import { SidebarTransferMenu } from "../SidebarTransferMenu";
import { StorageScope } from "../../../../services/api/instances/storageApi/types/service_types";
import styles from "./styles.module.css";

interface Props {
  service: StorageScope;
  subtype: string;
  actions: TSidebarActionsMenu;
  closeMenu: (id: TMenuIds) => void;
  handleActionClick: (type: string, info: TEntityInfo | null) => void;
  handleOpenSubMenu: (
    event: React.MouseEvent,
    childId: TMenuIds,
    parentId: TMenuIds,
    subtype?: string,
  ) => void;
  folders: MenuItems;
  currentFolderId: string | null;
  handleTransfer: (id: string) => void;
  isReceivedObject?: boolean;
  readOnly?: boolean;
}

export const SidebarActionsMenu = ({
  service,
  subtype,
  actions,
  folders,
  currentFolderId,
  handleTransfer,
  handleActionClick,
  handleOpenSubMenu,
  closeMenu,
  readOnly,
  isReceivedObject,
}: Props) => {
  const sidebarMenu = useMenuSelectors("sidebar_item_actions_menu");
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  const actionsType = useMemo(() => {
    if (subtype === "parent") {
      return actions.parent.filter((a) =>
        !a.supported_services ? a : a.supported_services?.includes(service),
      );
    } else {
      return actions.child.filter((a) =>
        !a.supported_services ? a : a.supported_services?.includes(service),
      );
    }
  }, [subtype, actions.child, actions.parent]);

  useClickOutside(
    menuRef,
    () => {
      closeMenu("sidebar_item_actions_menu");
      closeMenu("sidebar_item_transfer_menu");
    },
    submenuRef,
    sidebarMenu.isOpen,
  );

  if (!sidebarMenu.isOpen) {
    return null;
  }

  return (
    <MenuBasic
      isOpen={sidebarMenu.isOpen}
      position={sidebarMenu.position}
      onClose={() => {
        closeMenu("sidebar_item_actions_menu");
        closeMenu("sidebar_item_transfer_menu");
      }}
      classes={{
        root: styles["menu-root"],
        paper: styles["menu-paper"],
      }}
      ref={menuRef}
    >
      {actionsType.map((action) => {
        return (
          <AppMenuItem
            key={action.type}
            onClick={() => handleActionClick(action.type, sidebarMenu.info)}
            onMouseEnter={(e) => {
              action.type === "transfer"
                ? handleOpenSubMenu(
                    e,
                    "sidebar_item_transfer_menu",
                    "sidebar_item_actions_menu",
                    "menu",
                  )
                : closeMenu("sidebar_item_transfer_menu");
            }}
            className={classNames(
              styles["action-item"],
              styles[`${action.type}-action`],
            )}
            disabled={
              (action.type === "transfer" && !folders?.length) ||
              ((action.type === "delete" || action.type === "share") &&
                isReceivedObject) ||
              (readOnly && service === "schemas" && action.type !== "copy")
            }
          >
            {action.icon}
            <div>{action.title}</div>
          </AppMenuItem>
        );
      })}
      <SidebarTransferMenu
        submenuRef={submenuRef}
        folders={folders}
        currentFolderId={currentFolderId}
        subtype={"menu"}
        handleTransfer={handleTransfer}
        closeMenu={closeMenu}
      />
    </MenuBasic>
  );
};
